import React from 'react';
import { MarkdownRenderer } from './markdown-renderer';
import { formatMessageTime } from '../../utils/chatUtils';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  userName: string;
  assistantName: string;
}

export const ChatMessageBubble: React.FC<MessageBubbleProps> = React.memo(({
  message,
  userName,
  assistantName
}) => {
  return (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      {/* Message Bubble */}
      <div className={`relative px-4 py-3 rounded-2xl ${message.type === 'user'
          ? 'bg-primary text-primary-foreground rounded-br-md max-w-[80%]'
          : message.isTyping 
              ? 'bg-muted text-foreground rounded-bl-md max-w-[80%] pr-4'
              : 'bg-muted text-foreground rounded-bl-md w-full'
          }`}>
        
        <div className="text-base leading-relaxed message-content">
          {message.type === 'assistant' ? (
            <div className="relative">
              <MarkdownRenderer content={message.content} />
              {message.isTyping && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <MarkdownRenderer content={message.content} isUserMessage={true} />
            </div>
          )}
        </div>

        {/* Timestamp */}
        <p className={`text-xs mt-2 ${message.type === 'user' 
          ? 'text-primary-foreground/70' 
          : 'text-muted-foreground'
        }`}>
          {message.type === 'user' ? userName : assistantName} • {formatMessageTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
});

ChatMessageBubble.displayName = 'ChatMessageBubble';

// Custom comparison function for React.memo
const areEqual = (prevProps: MessageBubbleProps, nextProps: MessageBubbleProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.type === nextProps.message.type &&
    prevProps.message.isTyping === nextProps.message.isTyping &&
    prevProps.userName === nextProps.userName &&
    prevProps.assistantName === nextProps.assistantName
  );
};

export default React.memo(ChatMessageBubble, areEqual);